import { Table, TableColumns } from '@bp/ui-components';
import { distance } from 'fastest-levenshtein';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SelectPlanungPersonTableDataType } from './SelectPlanungPersonTable';

export type PimProfileTableDataType = {
  uuid: string;
  firstName: string;
  lastName: string;
  userEmail?: string | null;
  profileEmail?: string | null;
};

type SelectPimProfileTableProps = {
  selectedPlanungPerson: SelectPlanungPersonTableDataType;
  onRowSelectionChange: (pimProfile: PimProfileTableDataType | null) => void;
  selectedPimProfile?: PimProfileTableDataType | null;
  pimProfilesData: PimProfileTableDataType[];
};

const MAX_DISTANCE = 2;
const MAX_LAST_NAME_DISTANCE = 2;
const MAX_FIRST_NAME_DISTANCE = 2;

export const SelectPimProfileTable = ({
  selectedPlanungPerson,
  onRowSelectionChange,
  selectedPimProfile,
  pimProfilesData,
}: SelectPimProfileTableProps) => {
  const { t } = useTranslation();

  const levenshteinResults = pimProfilesData
    .map((profile) => {
      const currentScore = { profile, score: Number.MAX_SAFE_INTEGER };
      if (selectedPlanungPerson.email && selectedPlanungPerson.email === profile.profileEmail) {
        currentScore.score = 0;
      }
      if (selectedPlanungPerson.email && selectedPlanungPerson.email === profile.userEmail && currentScore.score > 0) {
        currentScore.score = 0;
      }
      const score = distance(
        `${selectedPlanungPerson.firstName} ${selectedPlanungPerson.lastName}`,
        `${profile.firstName} ${profile.lastName}`,
      );
      if (score < currentScore.score) {
        currentScore.score = score;
      }
      if (distance(selectedPlanungPerson.lastName, profile.lastName) <= MAX_LAST_NAME_DISTANCE) {
        const score = distance(selectedPlanungPerson.lastName, profile.lastName);
        if (score < currentScore.score) {
          currentScore.score = score + 1;
        }
      }
      if (distance(selectedPlanungPerson.firstName, profile.firstName) <= MAX_FIRST_NAME_DISTANCE) {
        const score = distance(selectedPlanungPerson.firstName, profile.firstName);
        if (score < currentScore.score) {
          currentScore.score = score + 1;
        }
      }

      return currentScore;
    })
    .filter(({ score }) => score <= MAX_DISTANCE)
    .sort((a, b) => a.score - b.score);
  const data: PimProfileTableDataType[] = useMemo(() => {
    return (
      levenshteinResults?.map((levenshteinResult) => ({
        uuid: levenshteinResult.profile.uuid,
        firstName: levenshteinResult.profile.firstName,
        lastName: levenshteinResult.profile.lastName,
        userEmail: levenshteinResult.profile.userEmail,
        profileEmail: levenshteinResult.profile.profileEmail,
      })) ?? []
    );
  }, [levenshteinResults]);

  const columns: TableColumns<PimProfileTableDataType>[] = [
    {
      header: t('common.firstName'),
      id: 'firstName',
      accessorKey: 'firstName',
    },
    {
      header: t('common.lastName'),
      id: 'lastName',
      accessorKey: 'lastName',
    },
    {
      header: t('common.email'),
      id: 'userEmail',
      accessorKey: 'userEmail',
    },
  ];

  const currentIndex = data.findIndex((p) => p.uuid === selectedPimProfile?.uuid);
  const currentIndexObject = currentIndex >= 0 ? { [currentIndex]: true } : {};
  return (
    <>
      <Table<PimProfileTableDataType>
        showActionBar={true}
        actionBarSettings={{
          showGlobalFilter: false,
        }}
        emptyStateSettings={{
          title: t('users.noLoginFound'),
          subtitle: '',
        }}
        data={data}
        columns={columns}
        canScroll={false}
        isOnWhite={true}
        selectOnClick={true}
        showSelect={true}
        showSelected
        rowSelection={currentIndexObject}
        onRowSelectionChange={(updaterOrValue) => {
          const selection = typeof updaterOrValue === 'function' ? updaterOrValue({}) : updaterOrValue;
          const selectedIndex = parseInt(Object.keys(selection)[0]);
          const selectedPimProfile = data.find((item, key) => key === selectedIndex);
          if (selectedPimProfile) {
            onRowSelectionChange(selectedPimProfile);
          } else {
            onRowSelectionChange(null);
          }
        }}
      />
    </>
  );
};
